import type { CommandResult } from '@travel-planner/shared/schemas';
import type { WorkflowStepVisit } from '../model/use-agent-panel-store';
import { Badge, Button, Heading, Text } from '@/shared/ui';
import { useAgentPanelStore } from '../model/use-agent-panel-store';
import css from './workflow-summary-card.module.scss';

export type WorkflowSummaryCardProps = {
  // Called when the consultant clicks "Close" on the summary. Typically
  // wired to clear the workflow + close the agent panel.
  onClose?: () => void;
};

// Final-step summary card. Renders only when the workflow is done. Knows
// the shape of plan_trip's two steps (hotel → product picks) and pulls the
// relevant fields out of each step's result + the workflow's visit
// selections. Other workflows would want their own card; this is
// intentionally specific so the layout can match each workflow's data.
export const WorkflowSummaryCard = ({ onClose }: WorkflowSummaryCardProps) => {
  const ws = useAgentPanelStore((s) => s.workflowSession);
  if (!ws || ws.phase !== 'workflow-done') return null;

  const hotel = readHotelResult(ws.completedResults);
  const visitGroups = groupVisitsByLabel(ws.visits);

  return (
    <div className={css.root} data-testid="workflow-summary-card">
      <div className={css.title}>
        <span aria-hidden="true" className={css.titleEmoji}>
          ✈️
        </span>
        <Heading as="h3" size="md">
          Your trip plan
        </Heading>
      </div>

      <section className={css.section}>
        <Text className={css.sectionHeading} weight="semibold">
          🏨 Hotel
        </Text>
        {hotel ? (
          <>
            <div className={css.sectionRow}>
              <Text className={css.sectionKey} size="xs" variant="muted">
                City
              </Text>
              <Text size="sm" weight="medium">
                {hotel.city ?? '—'}
              </Text>
            </div>
            <div className={css.sectionRow}>
              <Text className={css.sectionKey} size="xs" variant="muted">
                Dates
              </Text>
              <Text size="sm" weight="medium">
                {hotel.checkIn ?? '—'} → {hotel.checkOut ?? '—'}
                {typeof hotel.nights === 'number'
                  ? ` (${hotel.nights} night${hotel.nights === 1 ? '' : 's'})`
                  : ''}
              </Text>
            </div>
            <div className={css.sectionRow}>
              <Text className={css.sectionKey} size="xs" variant="muted">
                Guests
              </Text>
              <Text size="sm" weight="medium">
                {hotel.guests ?? '—'}
              </Text>
            </div>
          </>
        ) : (
          <Text variant="muted">(skipped or unavailable)</Text>
        )}
      </section>

      <section className={css.section}>
        <Text className={css.sectionHeading} weight="semibold">
          🎯 Picks
        </Text>
        {visitGroups.length === 0 ? (
          <Text variant="muted">(no products picked)</Text>
        ) : (
          visitGroups.map((group, i) => (
            <div className={css.picksGroup} key={`${group.label}-${i}`}>
              <Badge variant="active">{group.label}</Badge>
              <ul className={css.picksList}>
                {group.selections.map((s) => {
                  const subtitle = [s.city, s.country]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <li className={css.pickRow} key={s.id}>
                      {s.imageUrl ? (
                        <img
                          alt=""
                          className={css.pickThumb}
                          src={s.imageUrl}
                        />
                      ) : (
                        <div
                          aria-hidden="true"
                          className={css.pickThumbPlaceholder}
                        />
                      )}
                      <div className={css.pickBody}>
                        <Text
                          className={css.pickTitle}
                          size="sm"
                          weight="medium"
                        >
                          {s.title}
                        </Text>
                        {subtitle && (
                          <Text
                            className={css.pickSubtitle}
                            size="xs"
                            variant="muted"
                          >
                            {subtitle}
                          </Text>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </section>

      {onClose && (
        <div className={css.actions}>
          <Button onClick={onClose} type="button" variant="primary">
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Internal: pull the fields we render out of each step's CommandResult.data.
// We rely on the documented shapes from the stub commands' execute fns:
//   hotel_availability → data.params {city, checkIn, checkOut, guests} + data.nights
// The shape is stable for the log-only stub; when the command gains a real
// integration, this reader needs to map the new field names too.
// ---------------------------------------------------------------------------

const readHotelResult = (
  results: ReadonlyArray<CommandResult>
): {
  city: string | null;
  checkIn: string | null;
  checkOut: string | null;
  guests: number | null;
  nights: number | null;
} | null => {
  const hotel = results.find((r) => r.kind === 'hotel_availability');
  if (!hotel) return null;
  const data = hotel.data as {
    params?: {
      city?: string;
      checkIn?: string;
      checkOut?: string;
      guests?: number;
    };
    nights?: number;
  };
  const params = data.params ?? {};
  return {
    city: params.city ?? null,
    checkIn: params.checkIn ?? null,
    checkOut: params.checkOut ?? null,
    guests: typeof params.guests === 'number' ? params.guests : null,
    nights: typeof data.nights === 'number' ? data.nights : null,
  };
};

// Map a raw category id (or any visit label) to the display group used in
// the trip summary: "Experiences" for experiences/activity/attraction,
// "Restaurants" for restaurants/dining, otherwise the title-cased label
// itself, falling back to "Picks" when no label is set.
const TOP_LEVEL_LABELS: Record<string, string> = {
  experiences: 'Experiences',
  activity: 'Experiences',
  attraction: 'Experiences',
  restaurants: 'Restaurants',
  dining: 'Restaurants',
};

const headerForLabel = (raw: string | null): string => {
  if (!raw) return 'Picks';
  const lower = raw.toLowerCase();
  if (lower in TOP_LEVEL_LABELS) {
    const value = TOP_LEVEL_LABELS[lower];
    if (value) return value;
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// Group visits sharing the same display group (Experiences vs Restaurants
// today; future top-level chips collapse the same way). The order
// preserves first-occurrence so the summary reads in the order the
// consultant picked.
const groupVisitsByLabel = (
  visits: ReadonlyArray<WorkflowStepVisit>
): Array<{ label: string; selections: WorkflowStepVisit['selections'] }> => {
  const groups = new Map<string, WorkflowStepVisit['selections']>();
  for (const v of visits) {
    const label = headerForLabel(v.label);
    const existing = groups.get(label) ?? [];
    groups.set(label, [...existing, ...v.selections]);
  }
  return [...groups.entries()].map(([label, selections]) => ({
    label,
    selections,
  }));
};
